import { useState, useEffect } from 'react';
import { Modal, Button, ListGroup, Badge } from 'react-bootstrap';
import { useAuth } from '@/hooks/useAuth';
import { InviteService } from '@/services/invite.service';
import type { CollaborationInvite } from '@/types';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

export function InviteNotifications() {
    const { user } = useAuth();
    const [invites, setInvites] = useState<CollaborationInvite[]>([]);
    const [showModal, setShowModal] = useState(false);
    const [processingId, setProcessingId] = useState<string | null>(null);

    useEffect(() => {
        if (!user) return;

        const unsubscribe = InviteService.subscribeToPendingInvites(user.id, (pendingInvites) => {
            setInvites(pendingInvites);
        });

        return () => unsubscribe();
    }, [user]);

    const handleAccept = async (invite: CollaborationInvite) => {
        setProcessingId(invite.id);
        try {
            await InviteService.acceptInvite(invite.id);
            // Optimistic update handled by subscription
        } catch (err) {
            console.error('Failed to accept invite', err);
            alert('Failed to accept invite');
        } finally {
            setProcessingId(null);
        }
    };

    const handleDecline = async (invite: CollaborationInvite) => {
        if (!confirm('Are you sure you want to decline this invitation?')) return;
        setProcessingId(invite.id);
        try {
            await InviteService.declineInvite(invite.id);
        } catch (err) {
            console.error('Failed to decline invite', err);
            alert('Failed to decline invite');
        } finally {
            setProcessingId(null);
        }
    };

    if (!user) return null;

    return (
        <>
            {/* Notification Trigger - Designed to fit in sidebar/nav */}
            <div
                className="d-flex align-items-center gap-2 cursor-pointer text-decoration-none text-white px-3 py-2 rounded position-relative"
                onClick={() => setShowModal(true)}
                style={{ cursor: 'pointer' }}
                title="Pending Invitations"
            >
                <i className={`bi ${invites.length > 0 ? 'bi-envelope-exclamation-fill text-warning' : 'bi-envelope'} fs-5`}></i>
                <span className="d-none d-md-inline">Invitations</span>
                {invites.length > 0 && (
                    <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger" style={{ left: '85% !important' }}>
                        {invites.length}
                        <span className="visually-hidden">unread messages</span>
                    </span>
                )}
            </div>

            {/* Invites Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Pending Invitations</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {invites.length === 0 ? (
                        <div className="text-center py-4 text-muted">
                            <i className="bi bi-envelope-open display-4 mb-3 d-block opacity-50"></i>
                            <p className="mb-0">No pending invitations.</p>
                        </div>
                    ) : (
                        <ListGroup variant="flush">
                            {invites.map(invite => (
                                <ListGroup.Item key={invite.id} className="d-flex flex-column gap-2 py-3">
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div>
                                            <strong>{invite.inviterName}</strong> invited you to join <strong>{invite.playlistName}</strong>
                                            <div className="text-muted small mt-1">
                                                {new Date(invite.createdAt).toLocaleDateString()}
                                            </div>
                                        </div>
                                        {invite.status === 'pending' && (
                                            <Badge bg="warning" text="dark">Pending</Badge>
                                        )}
                                    </div>
                                    <div className="d-flex gap-2 justify-content-end mt-2">
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={() => handleDecline(invite)}
                                            disabled={processingId === invite.id}
                                        >
                                            Decline
                                        </Button>
                                        <Button
                                            variant="primary"
                                            size="sm"
                                            onClick={() => handleAccept(invite)}
                                            disabled={processingId === invite.id}
                                        >
                                            {processingId === invite.id ? <LoadingSpinner size="sm" /> : 'Accept'}
                                        </Button>
                                    </div>
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    )}
                </Modal.Body>
            </Modal>
        </>
    );
}
